import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { buildersAPI, projectsAPI, hireRequestsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  Star,
  MapPin,
  Search,
  CheckCircle2,
  Briefcase,
  Phone,
  Mail,
  Loader2,
  Filter,
  UserPlus,
} from 'lucide-react';

const BuildersPage = () => {
  const { user } = useAuth();
  const [builders, setBuilders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  
  // Hire modal state
  const [hireModalOpen, setHireModalOpen] = useState(false);
  const [selectedBuilder, setSelectedBuilder] = useState(null);
  const [hireForm, setHireForm] = useState({
    project_id: '',
    message: '',
    proposed_rate: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const skills = [
    'Foundation',
    'Masonry',
    'Concrete Work',
    'Carpentry',
    'Roofing',
    'Framing',
    'Electrical',
    'Wiring',
    'Solar Installation',
    'Plumbing',
    'Pipe Fitting',
    'Drainage',
    'Painting',
    'Plastering',
    'Tiling',
  ];

  useEffect(() => {
    loadBuilders();
    loadProjects();
  }, [selectedSkill, verifiedOnly]);

  const loadBuilders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedSkill && selectedSkill !== 'all') params.skill = selectedSkill;
      if (verifiedOnly) params.verified_only = true;
      
      const response = await buildersAPI.getAll(params);
      setBuilders(response.data.builders || []);
    } catch (error) {
      console.error('Failed to load builders:', error);
      toast.error('Failed to load builders');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const openHireModal = (builder) => {
    setSelectedBuilder(builder);
    setHireForm({ project_id: '', message: '', proposed_rate: builder.hourly_rate?.toString() || '' });
    setHireModalOpen(true);
  };

  const handleHireSubmit = async (e) => {
    e.preventDefault();
    if (!hireForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    
    setSubmitting(true);
    try {
      await hireRequestsAPI.create({
        project_id: hireForm.project_id,
        builder_id: selectedBuilder.builder_id,
        message: hireForm.message || null,
        proposed_rate: hireForm.proposed_rate ? parseFloat(hireForm.proposed_rate) : null,
      });
      toast.success('Hire request sent successfully!');
      setHireModalOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send hire request';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredBuilders = builders.filter((builder) =>
    builder.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    builder.skills?.some((skill) => skill.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="builders-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Find Builders</h1>
          <p className="text-muted-foreground mt-1">
            Connect with verified builders for your construction project
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or skill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-builders-input"
            />
          </div>
          
          <Select value={selectedSkill} onValueChange={setSelectedSkill}>
            <SelectTrigger className="w-full sm:w-48" data-testid="skill-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by skill" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Skills</SelectItem>
              {skills.map((skill) => (
                <SelectItem key={skill} value={skill}>{skill}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant={verifiedOnly ? 'default' : 'outline'}
            onClick={() => setVerifiedOnly(!verifiedOnly)}
            className="whitespace-nowrap"
            data-testid="verified-filter"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Verified Only
          </Button>
        </div>

        {/* Builders Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredBuilders.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBuilders.map((builder) => (
              <Card 
                key={builder.builder_id} 
                className="border border-border hover:border-secondary/50 transition-colors"
                data-testid={`builder-card-${builder.builder_id}`}
              >
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 bg-[#0F2640] rounded-full flex items-center justify-center text-white font-heading font-bold text-lg">
                        {builder.user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <h3 className="font-heading font-bold">{builder.user?.name}</h3>
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="w-4 h-4 text-amber-400 fill-current" />
                          <span className="font-medium">{builder.rating?.toFixed(1)}</span>
                          <span className="text-muted-foreground">
                            ({builder.total_projects} projects)
                          </span>
                        </div>
                      </div>
                    </div>
                    {builder.verified && (
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-1 text-xs font-medium rounded flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                  </div>

                  {/* Bio */}
                  {builder.bio && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {builder.bio}
                    </p>
                  )}

                  {/* Skills */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {builder.skills?.slice(0, 4).map((skill) => (
                      <span
                        key={skill}
                        className="bg-muted text-muted-foreground px-2 py-1 text-xs rounded"
                      >
                        {skill}
                      </span>
                    ))}
                    {builder.skills?.length > 4 && (
                      <span className="text-xs text-muted-foreground">
                        +{builder.skills.length - 4} more
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    {builder.user?.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{builder.user.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      <span>{builder.experience_years} years experience</span>
                    </div>
                    {builder.hourly_rate > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-foreground">
                          BWP {builder.hourly_rate}/hr
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Availability */}
                  <div className={`text-center py-2 rounded-sm mb-4 ${
                    builder.availability 
                      ? 'bg-emerald-50 text-emerald-700' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {builder.availability ? 'Available for hire' : 'Currently busy'}
                  </div>

                  {/* Contact */}
                  <div className="flex gap-2">
                    {user?.role === 'landowner' && builder.availability && (
                      <Button
                        size="sm"
                        className="flex-1 bg-[#10B981] hover:bg-[#0ea572]"
                        onClick={() => openHireModal(builder)}
                        data-testid={`hire-builder-${builder.builder_id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Hire
                      </Button>
                    )}
                    {builder.user?.phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        asChild
                      >
                        <a href={`tel:${builder.user.phone}`}>
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </a>
                      </Button>
                    )}
                    {builder.user?.email && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        asChild
                      >
                        <a href={`mailto:${builder.user.email}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border border-border">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg mb-2">No builders found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search or filters
              </p>
            </CardContent>
          </Card>
        )}

        {/* Hire Builder Modal */}
        <Dialog open={hireModalOpen} onOpenChange={setHireModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Hire {selectedBuilder?.user?.name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleHireSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Select Project *</Label>
                <Select 
                  value={hireForm.project_id} 
                  onValueChange={(v) => setHireForm({...hireForm, project_id: v})}
                >
                  <SelectTrigger data-testid="hire-project-select">
                    <SelectValue placeholder="Choose a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length > 0 ? (
                      projects.map((project) => (
                        <SelectItem key={project.project_id} value={project.project_id}>
                          {project.title}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>No projects available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {projects.length === 0 && (
                  <p className="text-xs text-amber-600">Create a project first to hire builders</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Proposed Rate (BWP/hr)</Label>
                <Input
                  type="number"
                  value={hireForm.proposed_rate}
                  onChange={(e) => setHireForm({...hireForm, proposed_rate: e.target.value})}
                  placeholder="e.g., 150"
                  data-testid="hire-rate-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Message (Optional)</Label>
                <Textarea
                  value={hireForm.message}
                  onChange={(e) => setHireForm({...hireForm, message: e.target.value})}
                  placeholder="Describe your project requirements..."
                  rows={3}
                  data-testid="hire-message-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setHireModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !hireForm.project_id || projects.length === 0}
                  className="bg-[#10B981] hover:bg-[#0ea572]"
                  data-testid="submit-hire-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Request'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BuildersPage;
